import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDataGridSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

const FALLBACK_AUDIT_USER_ID = 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee';

const GLSystemDrawer = forwardRef(({ tenantId, refreshTrigger, onSelectionChange, hideToolbar = false }, ref) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.TENANT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [allSystems, setAllSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [form, setForm] = useState({
    systemId: '',
    name: '',
    surrogateKey: '',
    glOutputView: '',
  });

  const fetchTenantSystems = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!resp.ok) throw new Error('Failed to fetch GL systems');
      const data = await resp.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch GL systems');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSystems = async () => {
    setSystemsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/systems`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!resp.ok) throw new Error('Failed to fetch systems');
      const data = await resp.json();
      setAllSystems(Array.isArray(data) ? data : []);
    } catch (err) {
      setAllSystems([]);
    } finally {
      setSystemsLoading(false);
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    fetchTenantSystems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, refreshTrigger]);

  const openAddDialog = () => {
    if (!canAdd) return;
    setIsEditMode(false);
    setSelectedRow(null);
    setForm({ systemId: '', name: '', surrogateKey: '', glOutputView: '' });
    setDialogOpen(true);
    fetchAllSystems();
  };

  const openEditDialog = (row) => {
    if (!canEdit) return;
    setIsEditMode(true);
    setSelectedRow(row);
    setForm({
      systemId: row?.systemId || row?.system_id || '',
      name: row?.name || '',
      surrogateKey: row?.surrogateKey || row?.surrogate_key || '',
      glOutputView: row?.glOutputView ?? row?.gl_output_view ?? '',
    });
    setDialogOpen(true);
    fetchAllSystems();
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedRow(null);
    setForm({ systemId: '', name: '', surrogateKey: '', glOutputView: '' });
  };

  const openDeleteDialog = (row) => {
    if (!canDelete) return;
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openAddDialog: () => openAddDialog(),
    openEditDialog: () => {
      if (selectedRow) openEditDialog(selectedRow);
    },
    openDeleteDialog: () => {
      if (selectedRow) openDeleteDialog(selectedRow);
    }
  }), [selectedRow]);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRow);
    }
  }, [selectedRow, onSelectionChange]);

  useEffect(() => {
    if (!dialogOpen || form.systemId || !form.name || allSystems.length === 0) return;
    const selected = allSystems.find((sys) => sys.name === form.name);
    if (!selected) return;
    setForm((prev) => ({
      ...prev,
      systemId: selected.id,
      surrogateKey: prev.surrogateKey || selected.surrogateKey || selected.surrogate_key || '',
    }));
  }, [allSystems, dialogOpen, form.name, form.systemId]);

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'systemId') {
      const selected = allSystems.find((sys) => sys.id === value);
      setForm((prev) => ({
        systemId: value,
        name: selected?.name || '',
        surrogateKey: selected?.surrogateKey || selected?.surrogate_key || '',
        glOutputView: prev.glOutputView,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!tenantId || !form.systemId || !form.name) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const createdBy = localStorage.getItem('user_id') || FALLBACK_AUDIT_USER_ID;
      const updatedBy = localStorage.getItem('user_id') || FALLBACK_AUDIT_USER_ID;
      const surrogateKeyTrimmed = String(form.surrogateKey ?? '').trim();
      const glOutputViewTrimmed = String(form.glOutputView ?? '').trim();
      const payload = {
        tenantId,
        systemId: form.systemId,
        name: form.name,
        // Send '' when cleared — PUT treats JSON `null` as “do not update” (MapStruct IGNORE).
        surrogateKey: surrogateKeyTrimmed,
        glOutputView: glOutputViewTrimmed,
        createdBy,
        updatedBy,
      };

      const endpoint = isEditMode && selectedRow?.id
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/${selectedRow.id}`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems`;
      const method = isEditMode ? 'PUT' : 'POST';

      const resp = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) throw new Error(`Failed to ${isEditMode ? 'update' : 'add'} GL system`);

      closeDialog();
      fetchTenantSystems();
    } catch (err) {
      alert(err.message || 'Failed to save GL system');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow?.id) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/${selectedRow.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!resp.ok) throw new Error('Failed to delete GL system');
      closeDeleteDialog();
      fetchTenantSystems();
    } catch (err) {
      alert(err.message || 'Failed to delete GL system');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'GL System', flex: 1, minWidth: 180 },
    { field: 'surrogateKey', headerName: 'Surrogate Key', flex: 1, minWidth: 180 },
    {
      field: 'glOutputView',
      headerName: 'GL output view',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row?.glOutputView ?? params.row?.gl_output_view ?? '',
    },
  ];

  return (
    <Box sx={{ width: '100%', height: 240, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {!hideToolbar && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, pr: 1 }}>
          <IconButton
            size="small"
            onClick={() => selectedRow && openEditDialog(selectedRow)}
            disabled={!selectedRow || !canEdit}
            title="Edit"
          >
            <EditOutlinedIcon fontSize="small" sx={{ color: ht.text }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => selectedRow && openDeleteDialog(selectedRow)}
            disabled={!selectedRow || !canDelete}
            sx={{ color: 'error.main' }}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={openAddDialog}
            disabled={!canAdd}
            title="Add"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={35}
          columnResizeMode="onEnd"
          resizable
          disableSelectionOnClick
          sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
          onRowSelectionModelChange={(newSelection) => {
            if (newSelection.length > 0) {
              const row = rows.find((item) => item.id === newSelection[0]);
              setSelectedRow(row || null);
            } else {
              setSelectedRow(null);
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>No GL systems found.</Typography>
              </Box>
            )
          }}
          pagination
          pageSize={100}
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{isEditMode ? 'Edit GL System' : 'Add GL System'}</DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              select
              label="Name"
              name="systemId"
              value={form.systemId}
              onChange={handleFormChange}
              required
              fullWidth
              size="small"
              disabled={systemsLoading || submitting}
              helperText={systemsLoading ? 'Loading systems...' : ''}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              {allSystems.map((system) => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Surrogate Key"
              name="surrogateKey"
              value={form.surrogateKey}
              onChange={handleFormChange}
              fullWidth
              size="small"
              disabled={submitting}
              helperText="Pipe-separated header tokens only, e.g. GL_CODE|TRANSACTION_TYPE"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            <TextField
              label="GL output view"
              name="glOutputView"
              value={form.glOutputView}
              onChange={handleFormChange}
              fullWidth
              size="small"
              disabled={submitting}
              placeholder="Snowflake / DB view name (optional)"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting || !form.systemId || !form.name} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete GL System</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Delete "{selectedRow?.name}" from this tenant?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={submitting} sx={harmonateAbortButtonSx}>
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default GLSystemDrawer;
